import {
  DEFAULT_THEME,
  THEME_CONFIG_MESSAGE_TYPE,
  type ThemeConfigMessage,
  type ThemeState,
} from "@superbright/indexeddb-orm";

import { isAllowedCmsOrigin } from "utils/cmsOrigin";

import {
  applyThemeTokens,
  getThemeTokens,
  registerThemeFonts,
} from "./theme/themeConfig";

let lastFontKey = "";

function applyThemeConfig(config: ThemeState) {
  applyThemeTokens(getThemeTokens(config));

  // Re-register fonts only when the heading/body selection actually changes;
  // otherwise every color tweak would trigger a flash of invisible text.
  const fontKey = `${config.heading_font}|${config.body_font}`;
  if (fontKey !== lastFontKey) {
    registerThemeFonts(config);
    lastFontKey = fontKey;
  }
}

function isThemeMessage(data: unknown): data is ThemeConfigMessage {
  if (!data || typeof data !== "object") return false;
  const msg = data as ThemeConfigMessage;
  return (
    msg.type === THEME_CONFIG_MESSAGE_TYPE &&
    !!msg.config &&
    typeof msg.config === "object"
  );
}

export const EMBED_READY_MESSAGE_TYPE = "EMBED_READY";

export function initCmsStylerListener() {
  const isPreviewMode =
    new URLSearchParams(window.location.search).get("mode") === "preview";
  if (!isPreviewMode) return;

  // Only apply defaults if SSR hasn't already baked real theme tokens.
  // Avoids a flash of DEFAULT_THEME (black) before the first postMessage.
  const hasSSRTokens =
    !!document.documentElement.style.getPropertyValue("--color-bg");
  if (!hasSSRTokens) {
    applyThemeConfig(DEFAULT_THEME);
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (!isAllowedCmsOrigin(event.origin)) return;
    if (!isThemeMessage(event.data)) return;
    applyThemeConfig(event.data.config);
  });

  // Signal to the parent CMS that the listener is ready to receive the theme.
  // This resolves the race where postMessage fires before this listener registers.
  window.parent.postMessage({ type: EMBED_READY_MESSAGE_TYPE }, "*");
}
